<script setup>
import { ref, getCurrentInstance, onMounted, watch } from 'vue'
import {useRoute, useRouter} from "vue-router";
import { storeToRefs } from 'pinia'
import { useUserStore } from "../../stores/user";
import { useNavigationStore } from "../../../../stores/navigation";
import { usePageLoadingStore } from "../../../../stores/pageLoading";
import { useAlertStore } from "../../../../stores/alertStore";
import JobFairCorpInfoModal from "../../../../pages/user/components/modal/jobFair/JobFairCorpInfoModal.vue";
import mypageCorpInfoService from "../../services/mypage/mypageCorpInfoService";

const { $alert, $confirm } = useAlertStore();

const userStore = useUserStore();
const { user, isAuthenticated } = storeToRefs(userStore);

const navigationStore = useNavigationStore();
const globals = getCurrentInstance().appContext.config.globalProperties;
const { $pageListBoardNo } = globals;

const route = useRoute()
const router = useRouter()
const loadingStore = usePageLoadingStore()

const list = ref([]);
const currentPage = ref(0);
const pageSize = ref(20);
const totalPages = ref(1);
const totalCount = ref(0);
const searchOpen = ref(true);

const industryGbOptions = ref(globals.$industryGb || []);
const corpGbOptions = ref(globals.$corpGb || []);
const corpPublicYnOptions = ref(globals.$corpPublicYn || []);

const industryGbListRef = ref(null);
const corpGbListRef = ref(null);
const corpPublicYnListRef = ref(null);
const workAreaCorpRef = ref(null);

const corp = ref(null);
const corpInfoModal = ref(null); // 기업정보

let userCorpMasterYn = ref('N'); // 기업마스터유무
let userCorpId = ref(''); // 기업아이디
const isStudent = ref(false);

const onSelectedCorpArea1Option = (option) => {
  searchInput.value.workArea1 = option === '시/도 선택' ? '' : option;
};

const onSelectedCorpArea2Option = (option) => {
  searchInput.value.workArea2 = option === '시/구/군 선택' ? '' : option;
};

const initialSearchInput = {
  industryGbs: industryGbOptions.value.map(item => item.value),
  corpGbs: corpGbOptions.value.map(item => item.value),
  corpPublicYns: corpPublicYnOptions.value.map(item => item.value),
  bizType: '',
  workArea1: '',
  workArea2: '',
  corpName: '',
};

const searchInput = ref({ ...initialSearchInput });

const clearSearch = () => {
  Object.assign(searchInput.value, initialSearchInput);

  if (industryGbListRef.value && industryGbListRef.value.initOptions) {
    industryGbListRef.value.initOptions();
  }
  if (corpGbListRef.value && corpGbListRef.value.initOptions) {
    corpGbListRef.value.initOptions();
  }
  if (corpPublicYnListRef.value && corpPublicYnListRef.value.initOptions) {
    corpPublicYnListRef.value.initOptions();
  }
  if (workAreaCorpRef.value && workAreaCorpRef.value.initSelectedOption) {
    workAreaCorpRef.value.initSelectedOption('시/도 선택', '시/구/군 선택');
  }
};

const onClickSearch = () => {
  currentPage.value = 0;
  retrieveList();
};

const onSelectPage = (pageNo) => {
  currentPage.value = pageNo;
  retrieveList();
};

const onSelectPageSize = (option) => {
  pageSize.value = option.value;
  currentPage.value = 0;
  retrieveList();
};

// 정렬 셀렉트 박스 선택
const onSelectOrderBy = (option) => {
  loadingStore.loading = true;
  const [orderProperty, orderDirection] = option.value.split(" ");
  currentPage.value = 0;
  searchInput.value.orderProperty = orderProperty;
  searchInput.value.orderDirection = orderDirection;
  retrieveList();
};

const jobOrderByList = [
  { label: '최근 등록순', value: 'corporationInfoAgreePrivacyDate'},
  { label: '최근 수정순', value: 'modifyDate'},
];

const pageSizeGroup = ref([
  { label:"20개씩 보기", value: "20" },
  { label:"40개씩 보기", value: "40" },
  { label:"60개씩 보기", value: "60" },
  { label:"80개씩 보기", value: "80" },
  { label:"100개씩 보기", value: "100" },
]);

const retrieveList = () => {
  const params = {
    searchIndustryGbs: searchInput?.value.industryGbs?.join(',') || "",
    searchCorpGbs: searchInput?.value.corpGbs?.join(',') || "",
    searchBizType: searchInput?.value.bizType || "",
    searchCorpPublicYn: searchInput?.value.corpPublicYns?.join(',') || "",
    searchResidences: `${searchInput?.value.workArea1 || ''}${searchInput?.value.workArea1 ? ' ' : ''}${searchInput?.value.workArea2 || ''}`,
    searchCorpName: searchInput?.value.corpName || "",
    pageNumber: currentPage.value,
    size: pageSize.value,
    userId: user.value?.userCd || '',
    orderProperty: searchInput.value.orderProperty,
    orderDirection: searchInput.value.orderDirection,
  };

  loadingStore.loading = true;
  mypageCorpInfoService.retrieveAllCorpoInfoPaging(params)
    .then(res => {
      if(res.data.code.toLowerCase() === 'success') {
        const result = res.data.result;
        totalPages.value = result.totalPages;
        totalCount.value = result.totalElements;
        list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
      }
      loadingStore.loading = false;
    })
    .catch( (error) =>  {
      loadingStore.loading = false;
    });
}

// 기업정보 보기
const onClickCorp = async (corp) => {
  let viewSource = 'promotion'
  let userType = 'none';
  let userId = user.value?.userCd || '';
  let corpId = corp.corpId;

  if (user.value?.userGb === 'STUDENT') {
    userType = 'student';
  } else if (user.value?.userGb === 'COMPANY') {
    if (userCorpId.value === corpId && userCorpMasterYn.value === 'Y') {
      userType = 'company';
    } else {
      userType = 'otherCompany'; // 버튼 안보이게 됨
    }
  }

  corpInfoModal.value.retrieveCorporation(corpId, userId, viewSource, userType);
  corpInfoModal.value.show();
}

// 기업정보 등록
const onClickEntryCorpInfo = async () => {
  if(!isAuthenticated.value) {
    // 로그인 X
    $alert("로그인 후 이용해주세요.");
    navigationStore.lastAccessPage = route.fullPath;
    router.push({name: 'Signin'});
  } else {
    if (user.value.userGb === 'COMPANY' && userCorpMasterYn.value === 'Y') {
      const corpId = user.value.userInfo.corporation.corpId
      mypageCorpInfoService.retrieveCorpInfoPrivacyAgreeYnByCorpId(corpId)
        .then( async (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            const result = res.data.result;

            if (result !== 'Y') {  // 기업정보 등록 없을 때
              router.push({ name: 'MyPageCorpInfoCreate' });
            } else {
              const ok = await $confirm(["등록된 기업정보가 있습니다."], "기업정보 관리", "확인");
              if (ok) {
                router.push({ name: 'MypageCorpPromotion' });
              }
            }
          }
        })
        .catch( (error) =>  {
          loadingStore.loading = false;
        });
    } else {
      $alert(["재직자/기업 마스터 회원만 기업정보 등록이 가능합니다.","(마스터 회원 문의는 Q&A게시판을 이용해 주세요.)"])
    }
  }
}

onMounted(() => {
  retrieveList();
});

watch(user, (newUser) => {
  if (newUser) {
    userCorpId.value = newUser.userInfo?.corporation?.corpId || '';
    userCorpMasterYn.value = newUser.userInfo?.corpMasterYn || 'N';
    isStudent.value = newUser.userGb === 'STUDENT';
  }
}, { immediate: true, deep: true });
</script>

<template>
  <div class="container">
    <div class="inner">
      <div class="title page-title">
        <h3>기업 홍보관</h3>
      </div>
      <div class="process-wrap">
        <ul>
          <li>- <b>구직을 희망하는 회원</b>은 ‘기업 홍보관’을 통해 언제든 <b>기업 정보를 확인하고 ‘상시 입사지원’을 진행</b> 할 수 있습니다.</li>
          <li>- <b>구인을 희망하는 기업</b>은 ‘기업 홍보관’을 통해 언제든 <b>기업 정보를 등록하고 ‘상시 입사지원자 정보’를 받아</b> 볼 수 있습니다.</li>
          <li>※ 기업은 [기업정보 등록하기]를 통해 홍보를 할 수 있으며, 이미 특별채용관을 통해 정보가 등록된 기업들은 자동으로 홍보가 진행됩니다.</li>
        </ul>
        <div class="process-layout">
          <div class="process-steps-scroll">
            <div class="process-steps">
              <div class="step"><div><span class="tip">기업</span>기업정보 등록</div></div>
              <div class="step-arrow"><div></div></div>
              <div class="step"><div><span class="tip">구직자</span>입사지원</div></div>
              <div class="step-arrow"><div></div></div>
              <div class="step"><div><span class="tip">기업</span>이력서 확인</div></div>
              <div class="step-arrow"><div></div></div>
              <div class="step"><div><span class="tip">시스템</span>매칭 완료</div></div>
              <div class="step-arrow active"><div></div></div>
              <div class="step large active"><div>기업별<br>채용전형 진행</div></div>
            </div>
          </div>
        </div>
      </div>
      <!-- start: 검색 -->
      <div class="filter-wrap" :class="{'open': searchOpen}">
        <p @click="searchOpen = !searchOpen">검색</p>
        <div class="filter">
          <dl>
            <dt>산업군</dt>
            <dd>
              <KSACheckBoxGroup
                  ref="industryGbListRef"
                  className="mr-md"
                  name="industryGbs"
                  v-model="searchInput.industryGbs"
                  :options="industryGbOptions" />
            </dd>
          </dl>
          <dl>
            <dt>기업형태</dt>
            <dd>
              <KSACheckBoxGroup
                  ref="corpGbListRef"
                  className="mr-md"
                  name="corpGbs"
                  v-model="searchInput.corpGbs"
                  :options="corpGbOptions" />
            </dd>
          </dl>
          <dl>
            <dt>상장여부</dt>
            <dd>
              <KSACheckBoxGroup
                  ref="corpPublicYnListRef"
                  className="mr-md"
                  name="corpPublicYns"
                  v-model="searchInput.corpPublicYns"
                  :options="corpPublicYnOptions" />
            </dd>
          </dl>
          <dl>
            <dt>업종</dt>
            <dd>
              <KSAInput class="text-box" v-model="searchInput.bizType" placeholder="업종을 입력해주세요." />
            </dd>
          </dl>
          <dl>
            <dt>사업장소재지</dt>
            <dd>
              <KSASelectResidenceArea
                  class="select"
                  ref="workAreaCorpRef"
                  @selectOption1="onSelectedCorpArea1Option"
                  @selectOption2="onSelectedCorpArea2Option"
              />
            </dd>
          </dl>
          <dl>
            <dt>기업명</dt>
            <dd>
              <KSAInput class="text-box" v-model="searchInput.corpName" placeholder="기업명을 입력해주세요." />
            </dd>
          </dl>
          <div class="btn-wrap">
            <KSASecondaryButton class="gray_button" text="초기화" @click="clearSearch" />
            <KSAPrimaryButton text="검색" @click="onClickSearch" />
          </div>
        </div>
      </div>
      <!-- end: 검색 -->
      <!-- start: 필터 -->
      <div class="list-top">
        <p>기업정보 <span>{{ $number(totalCount)}}</span>개</p>
        <div class="grid">
          <KSAPrimaryButton text="기업정보 등록하기" class="mr-xs" @click="onClickEntryCorpInfo" />
          <KSASelect
              ref="searchOrderByRef"
              :options="jobOrderByList"
              class="select"
              @selectOption="onSelectOrderBy"
          />
          <KSASelect
              ref="searchPageSizeRef"
              :options="pageSizeGroup"
              class="select ml-xs"
              @selectOption="onSelectPageSize"
          />
        </div>
      </div>
      <!-- end: 필터 -->
      <div v-if="list.length > 0">
        <div class="corp-wrap">
          <dl v-for="corp in list" @click="onClickCorp(corp)">
            <dt>
              <KSAImage :src="corp.ciImgDir"/>
            </dt>
            <dd>
              <p class="text-over mb-xs">{{ corp.corpName }}</p>
              <p class="text-over"><small>업종</small> {{ corp.bizType }}</p>
              <p class="text-over"><small>기업형태</small> {{ $gbNameKR('CORP_GB',corp.corpGb) }}</p>
              <p class="text-over"><small>상장여부</small> {{ corp.corpPublicYn === 'Y' ? '상장' : '비상장' }}</p>
            </dd>
          </dl>
        </div>
        <Paginate
            v-if="list.length > 0"
            v-model="currentPage"
            :page-count="totalPages"
            :click-handler="onSelectPage"
        />
      </div>
      <div v-else class="lecture-none text-center">
        등록된 기업정보가 없습니다.
      </div>
    </div>
  </div>
  <JobFairCorpInfoModal ref="corpInfoModal"/> <!-- 기업정보 보기 -->
</template>

<style lang="scss" scoped>
@import "../../../../assets/scss/theme";
.process-wrap {
  ul {
    margin-bottom: 20px;
    li {
      b {
        display: inline-block;
        text-decoration: underline;
      }
    }
  }
  .process-layout {
    .process-steps-scroll {
      width: 100%;
    }
    .process-steps {
      width: 100%;
      .step {
        width: 70%;
      }
      .step-arrow {
        z-index: 1;
      }
    }
  }
}

.corp-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 22px;
  dl {
    flex: 1 1 calc(25% - 22px);
    max-width: calc(25% - 16px);
    height: 220px;
    border: 1px solid $gray-regular_color;
    border-radius: 6px;
    padding: 16px;
    cursor: pointer;
    dt {
      width: 100%;
      height: 70px;
      border: 1px solid $gray-regular_color;
      img {
        object-fit: contain;
      }
    }
    dd {
      position: relative;
      margin-top: 10px;
      .text-over {
        display: block;
      }
      h1 {
        font-size: 18px;
      }
      p {
        max-width: 100%;
        &.link {
          color: $blue_color;
        }
        &:last-of-type {
          font-size: 14px;
        }
        small {
          display: inline-block;
          width: 64px;
          font-size: 14px;
          color: $gray_color;
        }
      }
    }
  }
}
</style>
